import {signal, Signal, computed, ReadonlySignal, batch, effect, untracked} from '@preact/signals-core';

export type {Signal} from '@preact/signals-core';

/**
 * Read only Signal type.
 *
 * Required for Store type checking
 * [Signal] and [ReadonlySignal] have identical type shapes...
 *
 * [Signal] extends [ReadonlySignal] === true &&
 * [ReadonlySignal] extends [Signal] === true
 *
 * type [ComputedSignal] was created to have type shape difference so that extend checks can work
 *
 * [Signal] extends [ComputedSignal] === true &&
 * [ComputedSignal] extends [Signal] === **false**
 */
export type ComputedSignal<T = any> = ReadonlySignal<T> & {__fantom_data__: any;};

export type StoreDataMap = {
    [key: string]: Signal|ComputedSignal;
};

type ExtractSignalType<T> = T extends Signal< infer R > ? R : never;

type FilterComputedKeys<T extends StoreDataMap> = {
        [K in keyof T]-?: T[K] extends ComputedSignal ? K : never
}[keyof T];

type FilterSignals<T extends StoreDataMap> = Omit<T, FilterComputedKeys<T>>;

type SignalUpdateBatch<T extends StoreDataMap> = {[P in keyof FilterSignals<T>]?: ExtractSignalType<T[P]>};

export type UnwrapCanBeSignal<T> = T extends ComputedSignal<infer R> ? R : ExtractSignalType<T>;

type MapObjectOfSignalsToObjectOfData<T extends StoreDataMap> = {
    [KEY in keyof T]: UnwrapCanBeSignal<T[KEY]>
};

type MapObjectOfSignalsToObjectOfConditions<T extends StoreDataMap> = {
    [KEY in keyof T]: UnwrapCanBeSignal<T[KEY]> | ((val: UnwrapCanBeSignal<T[KEY]>) => void|boolean)
};

export type EffectOptions = {
    signal?: AbortSignal;
};

function effectWithOptions(fn: () => void, options?: EffectOptions): () => void {
    const off = effect(fn);
    const signal = options?.signal;

    if (signal) {
        if (signal.aborted) {
            throw new Error('Abort controller signal already aborted');
        }

        signal.addEventListener('abort', off);
    }

    return off;
}

/**
 * It is a datastore that uses signals to store data.
 * It also provides convenient tools for data management.
 */
export class Store <T extends StoreDataMap> {

    static readonly computed = computed as <T>(fn: () => T) => ComputedSignal<T>;
    static readonly signal = signal;
    static readonly batch = batch;
    static readonly effect = effectWithOptions;
    static readonly untracked = untracked;

    static stores: Array<[Store<any> & Readonly<any>, Record<string, any>]> = [];

    private readonly _values: MapObjectOfSignalsToObjectOfData<T>;
    private readonly _valuesPeek: MapObjectOfSignalsToObjectOfData<T>;

    constructor(
        public readonly data: T
    ) {
        const values = this._values = {} as MapObjectOfSignalsToObjectOfData<T>;

        for (const key in data) {
            if (Object.prototype.hasOwnProperty.call(data, key)) {
                const value = data[key];

                Object.defineProperty(values, key, {
                    get() {
                        return value.value;
                    },
                });
            }
        }

        const valuesPeek = this._valuesPeek = {} as MapObjectOfSignalsToObjectOfData<T>;

        for (const key in data) {
            if (Object.prototype.hasOwnProperty.call(data, key)) {
                const value = data[key];

                Object.defineProperty(valuesPeek, key, {
                    get() {
                        return value.peek();
                    },
                });
            }
        }
    }

    /**
     * Updates multiple signals at once.
     * This simplifies the code and optimizes performance.
     * If a computed depends on multiple signals
     * and these signals are updated using this method,
     * the computed will be recalculated only once.
     */
    batchUpdate(data: SignalUpdateBatch<T>): void {
        Store.batch(() => {
            // eslint-disable-next-line guard-for-in
            for (const key in data) {
                const val = (data as any)[key];

                if (val !== undefined && Object.prototype.hasOwnProperty.call(data, key)) {
                    (this.data[key] as Signal).value = val;
                }
            }
        });
    }

    /**
     * Returns a read-only value reader.
     * A collection of getters that *reads* signal values at the moment of access.
     * For example useful for destructors;
     * `const {valA, valB, valC} = store.values();`
     */
    values(): MapObjectOfSignalsToObjectOfData<T> {
        return this._values;
    }

    /**
     * Returns a read-only value reader.
     * A collection of getters that *peek* signal values at the moment of access.
     * For example useful for destructors;
     * `const {valA, valB, valC} = store.peek();`
     */
    peek(): MapObjectOfSignalsToObjectOfData<T> {
        return this._valuesPeek;
    }

    keys(): (keyof T)[] {
        return Object.keys(this.data) as (keyof T)[];
    }

    getSignals(): T {
        return this.data;
    }

    /**
     * Сreates a new datastore that is the sum of this store and the new data.
     */
    extend<X extends StoreDataMap>(data: X): Store<T&X> & Readonly<T&X>{
        Object
            .keys(data)
            .forEach(key => {
                if (this.data[key]) {
                    throw new Error(`[Store.extend(...)] error key "${key} already exists."`);
                }
            });

        // @ts-ignore
        return Store.createStore({...this.data, ...data});
    }

    /**
     * Creates a new read-only store, a subset of this store.
     * The new store will contain only the fields specified in the function call.
     */
    // eslint-disable-next-line @typescript-eslint/explicit-module-boundary-types
    extractReadOnlyStore<K extends keyof T>(keys: K[]) {
        const data: {[P in K]: ComputedSignal<UnwrapCanBeSignal<T[P]>> } = {} as any;

        keys.forEach(key => {
            data[key] = Store.computed(() => this.data[key].value) as any;
        });

        return Store.createStore(data);
    }

    /**
     * Provides access to data as if it were read-only
     * but in fact it is not read-only.
     * This is done using TypeScript types
     */
    // eslint-disable-next-line @typescript-eslint/explicit-module-boundary-types
    asReadonlySignalCollection() {
        // change only type for better performance
        return this.data as {[K in keyof T]: ComputedSignal<UnwrapCanBeSignal<T[K]>>};
    }

    /**
     * Creates a new store that is just a subset of this store.
     * The store will consist only of
     * the fields that were passed to function call.
     */
    // eslint-disable-next-line @typescript-eslint/explicit-module-boundary-types
    extractStoreSubset<K extends keyof T>(keys: K[]) {
        const data: {[P in K]: T[P] } = {} as any;

        keys.forEach(key => {
            data[key] = this.data[key];
        });

        return Store.createStore(data);
    }

    // eslint-disable-next-line @typescript-eslint/explicit-module-boundary-types
    asReadOnlyStore() {
        return Store.createStore(this.asReadonlySignalCollection());
    }

    /**
     * Method for comparing store values.
     * It is not recursive keep this in mind for objects and arrays.
     */
    is(dataToCompare: Partial<MapObjectOfSignalsToObjectOfConditions<T>>): boolean {
        for (const key in dataToCompare) {
            if (Object.prototype.hasOwnProperty.call(dataToCompare, key)) {
                const valToCompare = dataToCompare[key];
                const valFromStore = this.data[key].value;

                if (typeof valToCompare === 'function') {
                    if (!valToCompare(valFromStore)) {
                        return false;
                    } else {
                        continue;
                    }
                }

                if ( valToCompare !== valFromStore) {
                    return false;
                }
            }
        }

        return true;
    }

    /**
     * Use this method to create new stores,
     * this is required for correct typing.
     */
    static createStore<T extends StoreDataMap>(data: T): Store<T> & Readonly<T> {
        const store = new Store(data) as Store<T> & Readonly<T>;
        const backUp: Partial<{ [K in keyof T]: T[K] extends ReadonlySignal<any> ? never : T[K] }> = {};

        for (const key in data) {
            if (Object.prototype.hasOwnProperty.call(data, key)) {
                const value = data[key];

                Object.defineProperty(store, key, {value, writable: false});

                if (Store.isNotReadonlySignal(value)) {
                    backUp[key] = value.value;
                }
            }
        }

        Store.stores.push([store, backUp]);

        return store;
    }

    static resetStores(): void {
        Store.stores.forEach(([store, backUp]) => {
            store.batchUpdate(backUp);
        });
    }

    /**
     * Creates a new object where all values of the new object
     * are values from the previous object wrapped into signals.
     */
    // TODO: CHECK maybe to remove
    static mapObjectToSignals<T extends Record<string, any>, K extends keyof T, X extends {[P in K]: Signal<T[P]>}>(obj: T): X {
        const result = {} as X;

        for (const key in obj) {
            if (Object.prototype.hasOwnProperty.call(obj, key)) {
                const val = obj[key];

                (result as any)[key] = Store.signal(val);
            }
        }

        return result;
    }

    /**
     * Will return true if the passed value is a Signal or a Computed.
     */
    static isSignal<T>(val: T | Signal | ReadonlySignal): val is Signal | ReadonlySignal {
        return val instanceof Signal;
    }

    /**
     * Will return true if the passed value is a Computed.
     */
    static isReadonlySignal<T>(val: T | Signal | ReadonlySignal): val is ReadonlySignal {
        if (!Store.isSignal(val)) {
            return false;
        }

        const descriptor = Object.getOwnPropertyDescriptor(Object.getPrototypeOf(val), 'value');

        return !!descriptor && descriptor.set === undefined;
    }

    /**
     * Will return true if the passed value is a Signal but not a Computed.
     */
    static isNotReadonlySignal<T>(val: T | Signal | ReadonlySignal): val is Signal {
        if (!Store.isSignal(val)) {
            return false;
        }

        const descriptor = Object.getOwnPropertyDescriptor(Object.getPrototypeOf(val), 'value');

        return !!descriptor && descriptor.set !== undefined;
    }
}

